import { Injectable } from '@angular/core';

import { take } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { AsyncJobType } from '@recruitee/account-data';
import { AsyncJob, AsyncJobService, AsyncJobStatus } from '@recruitee/async-job-data';
import { RtcErrorHandler } from '@recruitee/common';

import { AsyncJobConfigService } from '../configs/async-job-config.service';
import { AsyncJobDialogService } from './async-job-dialog.service';
import { AsyncJobNotificationsService } from './async-job-notifications.service';

@Injectable({ providedIn: 'root' })
export class AsyncJobsDisplayController {
  constructor(
    private asyncJob: AsyncJobService,
    private asyncJobConfig: AsyncJobConfigService,
    private asyncJobNotificationsService: AsyncJobNotificationsService,
    private asyncJobDialogService: AsyncJobDialogService,
    private errorHandler: RtcErrorHandler,
  ) {
    this.init();
  }

  public init(): void {
    this.asyncJobNotificationsService.notificationClick$.subscribe(jobState => {
      const config = this.asyncJobConfig.get(jobState);
      const hasErrors = jobState.errors.length > 0;
      const canBeReopenedInDialog =
        config.showNotification && config.showDialog && jobState.status !== AsyncJobStatus.FINISHED;

      if (!this.asyncJobDialogService.isOpen() && (hasErrors || canBeReopenedInDialog)) {
        this.asyncJobDialogService.show();
      }
    });

    this.asyncJobDialogService.dialogClosed$
      .pipe(switchMap(() => this.asyncJob.getPendingJobs().pipe(take(1))))
      .subscribe(jobsInProgress => {
        jobsInProgress
          .filter(job => this.asyncJobConfig.get(job).showNotification)
          .forEach(job => this.asyncJobNotificationsService.show(job));
      });
  }

  public onJobStart(job: AsyncJob): void {
    if (this.asyncJobNotificationsService.isVisible(job)) return;

    const config = this.asyncJobConfig.get(job);
    // Debugging Sentry issue
    if (!config) {
      this.errorHandler.trace(
        `Missing job type: ${job.type}; Missing configs: ${JSON.stringify(job)}`,
        true,
      );
    }
    if (
      this.asyncJobNotificationsService.isEmpty() &&
      !this.asyncJobDialogService.isOpen() &&
      config.showDialog
    ) {
      this.asyncJobDialogService.show();
    }
    if (config.showNotification && (!this.asyncJobDialogService.isOpen() || !config.showDialog)) {
      this.asyncJobNotificationsService.show(job);
    }
  }

  public onJobFinish(job: AsyncJob): void {
    const config = this.asyncJobConfig.get(job);
    if (config.onFinish) {
      config.onFinish(job);
    }
  }

  public closePendingNotificationsByType(asyncJobType: AsyncJobType): void {
    this.asyncJob
      .getPendingJobs()
      .pipe(
        take(1),
        map(jobs => jobs.filter(job => job.type === asyncJobType).map(job => job.uuid)),
      )
      .subscribe(uuids => {
        uuids.forEach(uuid => this.asyncJobNotificationsService.closeNotification(uuid));
      });
  }
}
